import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaEye, FaSearch, FaBuilding, FaUserTie } from 'react-icons/fa';
import { Modal, Button, Badge } from 'react-bootstrap';
import '../pages_premium.css';
import AdminPagination from '../components/common/AdminPagination';

function HiremateJobRequestsList() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [jobData, setJobData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/hiremate-jobs/${jobId}/requests`);
            setJobData(res.data?.job || null);
            setRequests(Array.isArray(res.data?.requests) ? res.data.requests : []);
        } catch (error) {
            console.error('Error fetching job requests:', error);
            toast.error('Failed to fetch job requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (jobId) {
            fetchRequests();
        }
    }, [jobId]);

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const renderBadges = (val, colorScheme = 'blue') => {
        const parsed = parseJSON(val, Array.isArray(val) ? val : (typeof val === 'string' && val ? [val] : []));
        const list = Array.isArray(parsed) ? parsed : (typeof parsed === 'object' && parsed ? Object.keys(parsed) : [String(parsed)]);
        
        if (!list || list.length === 0) return <span style={{ color: '#94a3b8', fontSize: '13px' }}>-</span>;

        const bg = colorScheme === 'blue' ? '#e0f2fe' : (colorScheme === 'green' ? '#dcfce7' : '#f3e8ff');
        const color = colorScheme === 'blue' ? '#0369a1' : (colorScheme === 'green' ? '#15803d' : '#6b21a8');

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {list.map((item, idx) => (
                    <span key={idx} style={{
                        backgroundColor: bg,
                        color: color,
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        whiteSpace: 'nowrap'
                    }}>
                        {typeof item === 'object' ? (item.title || item.name || JSON.stringify(item)) : String(item)}
                    </span>
                ))}
            </div>
        );
    };

    const getStatusBadge = (statusStr) => {
        const s = String(statusStr || '').toLowerCase();
        let bg = '#e2e8f0';
        let color = '#475569';

        if (s === 'applied' || s === 'pending') {
            bg = '#e0f2fe'; color = '#0369a1';
        } else if (s === 'selected' || s === 'shortlisted' || s === 'accepted' || s === 'hired') {
            bg = '#dcfce7'; color = '#15803d';
        } else if (s === 'confirmed') {
            bg = '#15803d'; color = '#ffffff';
        } else if (s === 'under review' || s === 'need info' || s === 'trial' || s === 'still deciding') {
            bg = '#fef9c3'; color = '#a16207';
        } else if (s === 'backup') {
            bg = '#f3e8ff'; color = '#6b21a8';
        } else if (s === 'declined' || s === 'not suitable' || s === 'no show' || s === 'passed' || s === 'rejected') {
            bg = '#ffe4e6'; color = '#be123c';
        }

        return (
            <span style={{
                backgroundColor: bg,
                color: color,
                padding: '4px 10px',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '12px',
                textTransform: 'capitalize',
                display: 'inline-block',
                whiteSpace: 'nowrap'
            }}>
                {statusStr || 'Pending'}
            </span>
        );
    };

    const filtered = requests.filter(req => {
        const candidate = req.requested_by || '';
        const email = req.candidate_email || '';
        const company = req.company_name || '';
        const reqType = req.request_type || '';
        const status = req.status || '';
        const query = search.toLowerCase();

        return candidate.toLowerCase().includes(query) ||
            email.toLowerCase().includes(query) ||
            company.toLowerCase().includes(query) ||
            reqType.toLowerCase().includes(query) ||
            status.toLowerCase().includes(query);
    });

    const paginatedRequests = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setShowModal(true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return String(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    return (
        <div className="page-premium-wrapper hiremate-job-requests-page">
            <div className="page-premium-content">

                {/* Header Banner & Navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <button
                        onClick={() => navigate('/hiremate-jobs')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#0f172a',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    >
                        <FaArrowLeft /> Back to Jobs
                    </button>
                </div>

                {/* Job Summary Banner */}
                {jobData && (
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '20px 24px',
                        marginBottom: '24px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: '700', color: '#0f172a', fontSize: '1.4rem' }}>
                                    {jobData.title}
                                </h3>
                                <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '14px', color: '#64748b', flexWrap: 'wrap' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaBuilding style={{ color: '#0ab68e' }} /> {jobData.company_name}
                                    </span>
                                    <span>•</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaUserTie style={{ color: '#0ab68e' }} /> Posted by <strong>{jobData.posted_by}</strong> ({jobData.posted_by_email})
                                    </span>
                                    {jobData.pay_rate && (
                                        <>
                                            <span>•</span>
                                            <span>Pay: <strong>${parseFloat(jobData.pay_rate).toFixed(2)}</strong> {jobData.pay_type === 'hourly' ? '/ hr' : ''}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <Badge bg="info" style={{ fontSize: '14px', padding: '8px 14px', borderRadius: '10px' }}>
                                    {requests.length} Total Requests
                                </Badge>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Candidate Applications & Requests
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search candidate, email, request type or status..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading candidate requests...</div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th style={{ minWidth: '160px' }}>Job</th>
                                        <th style={{ minWidth: '180px' }}>Requested By</th>
                                        <th style={{ minWidth: '130px' }}>Request Type</th>
                                        <th style={{ minWidth: '150px' }}>Posted By</th>
                                        <th style={{ minWidth: '150px' }}>Company</th>
                                        <th style={{ minWidth: '180px' }}>Email</th>
                                        <th style={{ minWidth: '130px' }}>Phone</th>
                                        <th style={{ minWidth: '120px' }}>Request Date</th>
                                        <th style={{ minWidth: '120px' }}>Status</th>
                                        <th style={{ textAlign: 'center', width: '110px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRequests.map((reqItem, index) => (
                                        <tr key={reqItem.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + index + 1}</td>
                                            <td>
                                                <strong style={{ color: '#0f172a', fontSize: '0.88rem' }}>{reqItem.job_title}</strong>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {reqItem.candidate_avatar ? (
                                                        <img
                                                            src={reqItem.candidate_avatar}
                                                            alt={reqItem.requested_by}
                                                            style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1' }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#0ab68e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' }}>
                                                            {reqItem.requested_by ? reqItem.requested_by.charAt(0).toUpperCase() : 'C'}
                                                        </div>
                                                    )}
                                                    <strong style={{ color: '#0f172a', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{reqItem.requested_by}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{
                                                    backgroundColor: reqItem.request_type === 'Direct Invite' ? '#f3e8ff' : '#e0f2fe',
                                                    color: reqItem.request_type === 'Direct Invite' ? '#6b21a8' : '#0369a1',
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: '700',
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {reqItem.request_type}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ color: '#334155', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{reqItem.posted_by}</span>
                                            </td>
                                            <td>
                                                <strong style={{ color: '#334155', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{reqItem.company_name}</strong>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{reqItem.candidate_email}</span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{reqItem.candidate_phone}</span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#475569' }}>
                                                {formatDate(reqItem.request_date)}
                                            </td>
                                            <td>
                                                {getStatusBadge(reqItem.status)}
                                            </td>
                                            <td>
                                                <div className="hire-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => handleViewDetails(reqItem)}
                                                        className="action-btn action-btn-view"
                                                        data-tooltip="View Details"
                                                        style={{ backgroundColor: '#0ab68e', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="11">
                                                <div className="ptable-empty">
                                                    <div className="ptable-empty-icon">📨</div>
                                                    <p>No applications or requests found for this job.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="requests"
                    />
                </div>

            </div>

            {/* Request / Application Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <Modal.Title style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaUserTie style={{ color: '#0ab68e' }} /> Request & Candidate Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px', backgroundColor: '#fff' }}>
                    {selectedRequest && (
                        <div>
                            {/* Candidate Summary Header */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '20px', marginBottom: '20px' }}>
                                {selectedRequest.candidate_avatar ? (
                                    <img
                                        src={selectedRequest.candidate_avatar}
                                        alt={selectedRequest.requested_by}
                                        style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #0ab68e' }}
                                    />
                                ) : (
                                    <div style={{ width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#0ab68e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 'bold' }}>
                                        {selectedRequest.requested_by ? selectedRequest.requested_by.charAt(0).toUpperCase() : 'C'}
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <h4 style={{ margin: 0, fontWeight: '700', color: '#0f172a' }}>{selectedRequest.requested_by}</h4>
                                            <p style={{ margin: '4px 0', color: '#64748b', fontSize: '14px' }}>
                                                {selectedRequest.candidate_email} • {selectedRequest.candidate_phone}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                            <span style={{
                                                backgroundColor: selectedRequest.request_type === 'Direct Invite' ? '#f3e8ff' : '#e0f2fe',
                                                color: selectedRequest.request_type === 'Direct Invite' ? '#6b21a8' : '#0369a1',
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                fontWeight: '700',
                                                fontSize: '12px'
                                            }}>
                                                Type: {selectedRequest.request_type}
                                            </span>
                                            {getStatusBadge(selectedRequest.status)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Job Information */}
                            <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px', marginBottom: '20px' }}>
                                <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Job Overview</h6>
                                <p style={{ margin: '2px 0', fontSize: '14px', color: '#334155' }}>
                                    <strong>Position:</strong> {selectedRequest.job_title}
                                </p>
                                <p style={{ margin: '2px 0', fontSize: '14px', color: '#334155' }}>
                                    <strong>Company:</strong> {selectedRequest.company_name} (Posted by {selectedRequest.posted_by})
                                </p>
                                <p style={{ margin: '2px 0', fontSize: '14px', color: '#334155' }}>
                                    <strong>Request Date:</strong> {formatDate(selectedRequest.request_date)}
                                </p>
                            </div>

                            {/* Applicant / Invitation Message */}
                            {selectedRequest.cover_message && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h6 style={{ fontWeight: '700', color: '#334155', borderLeft: '3px solid #0ab68e', paddingLeft: '8px', marginBottom: '8px' }}>
                                        {selectedRequest.request_type === 'Direct Invite' ? 'Invitation Message' : 'Cover Message / Introduction'}
                                    </h6>
                                    <p style={{ color: '#475569', fontSize: '14px', lineHeight: '1.5', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px' }}>
                                        {selectedRequest.cover_message}
                                    </p>
                                </div>
                            )}

                            {/* Candidate Skills & Assessment Info */}
                            {selectedRequest.candidate_profile && (
                                <div>
                                    <div className="admin-2col-grid" style={{ gap: '16px', marginBottom: '20px' }}>
                                        <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                                            <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Categories & Roles</h6>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong style={{ fontSize: '12px', color: '#64748b' }}>Categories:</strong>
                                                <div style={{ marginTop: '4px' }}>{renderBadges(selectedRequest.candidate_profile.categories, 'blue')}</div>
                                            </div>
                                            <div>
                                                <strong style={{ fontSize: '12px', color: '#64748b' }}>Roles:</strong>
                                                <div style={{ marginTop: '4px' }}>{renderBadges(selectedRequest.candidate_profile.roles, 'purple')}</div>
                                            </div>
                                        </div>

                                        <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                                            <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Assessment Scores</h6>
                                            <Badge bg={selectedRequest.candidate_profile.overall_score >= 80 ? "success" : "warning"} style={{ fontSize: '12px', marginBottom: '8px' }}>
                                                Overall: {selectedRequest.candidate_profile.overall_score || 0}%
                                            </Badge>
                                            {typeof parseJSON(selectedRequest.candidate_profile.role_scores) === 'object' && Object.keys(parseJSON(selectedRequest.candidate_profile.role_scores)).length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                                                    {Object.entries(parseJSON(selectedRequest.candidate_profile.role_scores)).map(([role, score], idx) => (
                                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                            <span style={{ color: '#334155' }}>{role}</span>
                                                            <span style={{ color: '#0ab68e', fontWeight: '700' }}>{score}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Skills */}
                                    <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px', marginBottom: '20px' }}>
                                        <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Skills & Competencies</h6>
                                        {renderBadges(selectedRequest.candidate_profile.skills, 'green')}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e2e8f0', padding: '12px 24px' }}>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close Details
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default HiremateJobRequestsList;
